import { httpClientUser } from 'Api/microservice-http-clients';
import analytics from 'Analytics';
import { setItems, getItems, removeItems } from 'Utils/storage';
import CONFIG from 'Config';
import HttpClientError from 'Api/HttpClientError';
import RegisterHSModel from 'Models/RegisterHSModel';
import { AUTH_PROVIDERS } from 'Config/constans';
import { getAuthError } from 'Helpers/validations/auth-error-messages';
import AuthFirebase from './AuthFirebase';
import AuthAuth0 from './AuthAuth0';

export default class Auth {
  constructor() {
    this[AUTH_PROVIDERS.FIREBASE] = new AuthFirebase();
    this[AUTH_PROVIDERS.AUTH0] = new AuthAuth0();
    this.storageKeys = ['mfa', 'expirationTime', 'token', 'isEmailVerified'];
    this._auth = this[AUTH_PROVIDERS.AUTH0];
    this.initPromise = this.init();
    this.loginData = {};
    this.storageUpdateHandlerBinded = this.storageUpdateHandler.bind(this);
  }

  get token() {
    return this.loginData.token;
  }

  get expirationTime() {
    return this.loginData.expirationTime;
  }

  get provider() {
    return AUTH_PROVIDERS.AUTH0;
  }

  get isFirstLogin() {
    return this.loginData.isFirstLogin;
  }

  get canChangePassword() {
    return false;
  }

  getAuthHandler(provider) {
    switch (provider) {
      case AUTH_PROVIDERS.FIREBASE: {
        if (!this[AUTH_PROVIDERS.FIREBASE]) {
          this[AUTH_PROVIDERS.FIREBASE] = new AuthFirebase();
        }
        return this[AUTH_PROVIDERS.FIREBASE];
      }
      case AUTH_PROVIDERS.AUTH0: {
        if (!this[AUTH_PROVIDERS.AUTH0]) {
          this[AUTH_PROVIDERS.AUTH0] = new AuthAuth0();
        }
        return this[AUTH_PROVIDERS.AUTH0];
      }
      default:
        throw new Error('Wrong auth provider');
    }
  }

  async setAuthHandler(provider) {
    if (this.provider === provider) return this._auth;
    this._auth = this.getAuthHandler(provider);
    return this._auth;
  }

  async init() {
    await this.setAuthHandler(AUTH_PROVIDERS.AUTH0);
    this.setStorageUpdateHandler();
    const storageData = this.getStorageData();
    try {
      const { loginData, userData, isSocial, isMFA, isFirstLogin, isParsedHash } = await this._auth.init(storageData);
      if (isFirstLogin) {
        Object.assign(loginData, { isFirstLogin: true });
      }
      if (isParsedHash && !isMFA) {
        if (isSocial) await this.onSuccessLoginSocial({ loginData, userData });
        else await this.onSuccessLogin({ loginData });
      }
      this.setLogin(loginData);
      if (isMFA) {
        this.onSuccessMFASwitch(loginData.mfa);
      }
      return { loginData, userData };
    } catch (error) {
      // If user was login, logout to clear Auth0 session
      if (storageData.token) this.logout();
      throw error;
    }
  }

  async getAuthProviderByEmail(email = '') {
    const { data } = await httpClientUser.post('/login', { email: email.toLowerCase() });
    if (!data) throw new Error(getAuthError('user-not-found'));
    if (data.code) throw data;
    return {
      provider: data && data.auth_provider,
      social: data && data.social,
    };
  }

  async getIsAuthProviderFirebase(email) {
    try {
      const { provider } = await this.getAuthProviderByEmail(email);
      return provider === AUTH_PROVIDERS.FIREBASE;
    } catch (error) {
      return false;
    }
  }

  async getIsEmailInUse(email) {
    try {
      await this.getAuthProviderByEmail(email);
    } catch (e) {
      return true;
    }
    throw new HttpClientError({
      data: {
        code: 'user_exists',
        description: 'The user already exists.',
        statusCode: 400,
      },
    });
  }

  static sendHSRegister(data) {
    const registerHSModel = new RegisterHSModel();
    registerHSModel.$setAll(data);
    registerHSModel.send().catch(() => {});
  }

  isErrorOnReload() {
    const { hash } = window.location;
    return hash && hash.includes('error=') && hash.includes('state=');
  }

  setLogin(loginData, updateStorage = true) {
    if (updateStorage) {
      this.loginData = loginData;
      setItems(loginData, this.storageKeys, true);
    } else {
      Object.assign(this.loginData, loginData);
    }
  }

  getStorageData() {
    return getItems(this.storageKeys);
  }

  storageUpdateHandler({ key, newValue, oldValue }) {
    // do nothing if new value is the same as exist here
    // or waiting for reload
    if (this.loginData[key] === newValue || this.reloadTimeoutId) return;
    if (key === 'token' && newValue && !oldValue && newValue !== this.token) {
      this.reloadTimeoutId = setTimeout(() => {
        if (CONFIG.isEnvDev || CONFIG.isEnvStag) {
          // eslint-disable-next-line no-alert
          alert(`
            Page will be reload!
            --------------------------------------
            Reason: auth changed in another tab.
            --------------------------------------
            note: this message showing only on dev and staging
          `);
        }
        window.$__forceReload = true; // to do not confirm by user to reload page
        window.location.reload();
      }, 1000);
      return;
    }
    if (!this.storageKeys.includes(key)) return;
    const newLoginData = { [key]: newValue };
    this.setLogin(newLoginData, false);
    if (this.onLoginDataUpdate) this.onLoginDataUpdate(newLoginData);
  }

  removeLogin() {
    this.loginData = {};
    removeItems(this.storageKeys);
  }

  setStorageUpdateHandler() {
    window.addEventListener('storage', this.storageUpdateHandlerBinded);
  }

  async onSuccessRegister({ loginData, userData }) {
    this.setLogin(loginData);
    this.constructor.sendHSRegister({ email: loginData.email, ...userData });
    Object.assign(loginData, { isFirstLogin: true });
    return { loginData, userData };
  }

  async onSuccessRegisterSocial({ loginData, userData }) {
    analytics.triggerAccountCreated({ email: userData.email });
    return this.onSuccessRegister({ loginData, userData });
  }

  async onSuccessLogin({ loginData }) {
    try {
      this.setLogin(loginData);
      await httpClientUser.get('/login');
    } catch (e) {
      this.logout();
      throw e;
    }
  }

  async onSuccessLoginSocial({ loginData, userData }) {
    let regProvider;
    try {
      const result = await this.getAuthProviderByEmail(userData.email);
      regProvider = result.provider;
    } catch (e) {
      await this.onSuccessRegisterSocial({ loginData, userData });
      return { loginData, userData };
    }
    if (regProvider === AUTH_PROVIDERS.FIREBASE) throw new Error('Email already in use, please login below');
    await this.onSuccessLogin({ loginData });
    return { loginData, userData };
  }

  async login(email, password) {
    const args = [email, password];
    const isFirebase = await this.getIsAuthProviderFirebase(email);
    if (isFirebase) {
      args.push(AUTH_PROVIDERS.FIREBASE);
    }
    const { loginData } = await this._auth.login(...args);
    await this.onSuccessLogin({ loginData });
    return { loginData };
  }

  async register(email, password, formData) {
    const { loginData } = await this[AUTH_PROVIDERS.AUTH0].register(email, password, formData);
    return this.onSuccessRegister({ loginData, userData: formData });
  }

  async logout(...args) {
    await this._auth.logout(...args);
    this.setAuthHandler(AUTH_PROVIDERS.AUTH0);
    this.removeLogin();
  }

  async refreshToken() {
    const { loginData } = await this._auth.refreshToken();
    this.setLogin(loginData);
    return loginData;
  }

  async resetPassword(email) {
    try {
      const { provider } = await this.getAuthProviderByEmail(email);
      const authHandler = await this.getAuthHandler(provider);
      return authHandler.resetPassword(email);
    } catch (e) {
      return Promise.resolve();
    }
  }

  async socialLogin(...args) {
    try {
      const result = await this[AUTH_PROVIDERS.AUTH0].socialLogin(...args);
      return this.onSuccessLoginSocial(result);
    } catch (e) {
      this.logout();
      throw e;
    }
  }

  async parseErrorOnReload() {
    try {
      await this[AUTH_PROVIDERS.AUTH0]._parseHash();
      return null;
    } catch (e) {
      window.location.hash = '';
      return e;
    }
  }

  onSuccessMFASwitch(mfa) {
    httpClientUser.patch('/login', { mfa });
  }

  async enableMFA(forceReload) {
    // Pass is first login from login data to save it during mfa switching
    const result = await this._auth.enableMFA(false, {
      forceReload,
      isFirstLogin: this.isFirstLogin,
    });
    localStorage.setItem('mfa', true);
    this.onSuccessMFASwitch(true);
    return result;
  }

  async disableMFA() {
    const result = await this._auth.disableMFA();
    localStorage.removeItem('mfa');
    this.onSuccessMFASwitch(false);
    return result;
  }

  async sendEmailVerification() {
    await this._auth.sendEmailVerification();
    localStorage.setItem('email-verification-sent', new Date().toISOString());
  }

  getEmailVerificationSentLastDate() {
    const dateString = localStorage.getItem('email-verification-sent');
    if (!dateString) return null;
    return new Date(dateString);
  }
}
